import React, { useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { format, parseISO, differenceInMinutes } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useSchedule } from '../context/ScheduleContext';
import { getCurrentAndNextPerformances } from '../utils/timeUtils';
import { MapView, type MapLocation } from '../components/MapView';

export const Map: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { stages, allPerformances, virtualTime } = useSchedule();

  const highlightedStage = searchParams.get('stage') ?? undefined;

  const liveData = useMemo(
    () => getCurrentAndNextPerformances(allPerformances, virtualTime),
    [allPerformances, virtualTime]
  );

  const liveByStage = useMemo(
    () => Object.fromEntries(liveData.map((d) => [d.stage, d])),
    [liveData]
  );

  const locations: MapLocation[] = useMemo(
    () =>
      stages.map((s) => {
        const live = liveByStage[s.stage];
        return {
          lat: s.location.lat,
          lng: s.location.lng,
          label: s.stage,
          currentDj: live?.current?.dj,
          currentTime: live?.current
            ? `${format(parseISO(live.current.start_time), 'HH:mm')} - ${format(parseISO(live.current.end_time), 'HH:mm')}`
            : undefined,
          nextDj: live?.next?.dj,
          nextTime: live?.next
            ? (() => {
                const mins = differenceInMinutes(
                  parseISO(live.next.start_time),
                  virtualTime
                );
                if (mins > 59) {
                  const hrs = Math.floor(mins / 60);
                  const remainingMins = mins % 60;
                  const minutesPart =
                    remainingMins > 0
                      ? ` ${t('time.minutesShort', { minutes: remainingMins })}`
                      : '';
                  return t('time.hoursMinutesShort', { hours: hrs, minutesPart });
                }
                return t('time.minutesShort', { minutes: mins });
              })()
            : undefined,
        };
      }),
    [stages, liveByStage, virtualTime, t]
  );

  return (
    <div className="fixed inset-0 z-40 bg-background flex flex-col">
      <header className="flex items-center gap-2 p-4 border-b border-gray-800 bg-background/95 backdrop-blur shrink-0">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 text-gray-400 hover:text-white"
          aria-label={t('common.back')}
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold text-white flex-1">
          {t('map.stageMap')}
          {highlightedStage && (
            <span className="block text-sm font-normal text-gray-400">
              {highlightedStage}
            </span>
          )}
        </h1>
      </header>
      <div className="flex-1 min-h-0">
        <MapView
          locations={locations}
          highlightedStage={highlightedStage}
          onStageSelect={(s) => setSearchParams({ stage: s })}
        />
      </div>
    </div>
  );
};
